'use client'
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import './date-picker-styles.scss'
import styles from './styles.module.scss'

import DayPicker from 'react-day-picker'
import { CaptionElementProps } from 'react-day-picker/types/Props'
import DatePickerInput from '../DatePickerRange/DatePickerInput'
import { useHandleClickOutside } from '../DatePickerRange/useHandleClickOutside'
import { IHelperButton } from '../types'
import YearMonthForm from './YearMonthForm'
import {
  useLocaleMonths,
  useWeekdaysLong,
  useWeekdaysShort,
} from './useLocalesForDatePicker'

interface RangeDatePickerProps {
  onChange: (date: Date | undefined) => void
  value?: Date | undefined
  labelText?: string
  placeholder?: string
  name: string
  helperButton?: IHelperButton
  isDisabled?: boolean
  tooltip?: string | ReactNode
  disabledDaysBefore?: boolean
  hidePickerWhenChoose?: boolean
  mode?: 'light' | 'dark'
  errorWhenEmpty?: boolean
}

const DatePicker: React.FC<RangeDatePickerProps> = ({
  onChange,
  value,
  labelText,
  placeholder,
  isDisabled,
  helperButton,
  tooltip,
  disabledDaysBefore,
  hidePickerWhenChoose,
  mode,
  errorWhenEmpty = false,
}) => {
  const [isShown, setIsShown] = useState(false)
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)
  const pickerModalRef = useRef<HTMLDivElement>(null)
  const inputWrapperRef = useRef<HTMLDivElement>(null)
  const [pickerPosition, setPickerPosition] = useState<React.CSSProperties>()

  const months = useLocaleMonths()
  const weekdaysLong = useWeekdaysLong()
  const weekdaysShort = useWeekdaysShort()

  const onDayClickHandler = useCallback(
    (selectedDate: Date, modifies) => {
      if (!modifies.disabled) onChange(selectedDate)
      if (hidePickerWhenChoose) setIsShown(false)
    },
    [onChange, hidePickerWhenChoose, setIsShown],
  )

  const onResetInputClick = useCallback(() => {
    onChange(undefined)
    value && onChange(undefined)
  }, [onChange, value])

  const onDayPickerShow = useCallback(() => {
    setIsShown(true)
  }, [])

  const onDayPickerHide = useCallback(() => setIsShown(false), [])

  const handleClickOutside = useHandleClickOutside({
    pickerModalRef,
    action: onDayPickerHide,
  })

  const updatePickerPosition = useCallback(() => {
    const anchor = inputWrapperRef.current
    if (!anchor) return

    const anchorRect = anchor.getBoundingClientRect()
    const pickerRect = pickerModalRef.current?.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    const spacing = 8

    let top = anchorRect.bottom + spacing
    if (
      pickerRect?.height &&
      top + pickerRect.height > viewportHeight &&
      anchorRect.top - pickerRect.height - spacing > 0
    ) {
      top = anchorRect.top - pickerRect.height - spacing
    }

    let left = anchorRect.left
    if (pickerRect?.width && left + pickerRect.width > viewportWidth) {
      left = Math.max(spacing, viewportWidth - pickerRect.width - spacing)
    }

    setPickerPosition({
      position: 'fixed',
      top: Math.round(top),
      left: Math.round(left),
      transform: 'none',
      zIndex: 1000000,
    })
  }, [])

  useEffect(() => {
    setPortalTarget(document.body)
    value && onChange(value)
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!isShown) return

    updatePickerPosition()
    const raf = requestAnimationFrame(updatePickerPosition)

    const handleViewportChange = (event?: Event) => {
      const target = event?.target as Element | null
      if (target?.closest?.('.react-select__menu')) return
      updatePickerPosition()
    }
    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange, true)
    }
  }, [isShown, updatePickerPosition])

  const handleYearMonthChange = useCallback(
    (value: Date) => {
      onChange(value)
    },
    [onChange],
  )

  const initialMonth = new Date()
  initialMonth.setFullYear(initialMonth.getFullYear() - 18)

  return (
    <div className={styles.inputWrapper}>
      <div ref={inputWrapperRef}>
        <DatePickerInput
          label={labelText}
          placeholder={placeholder}
          onClick={onDayPickerShow}
          value={value}
          onResetClick={onResetInputClick}
          isDisabled={isDisabled}
          helperButton={helperButton}
          tooltip={tooltip}
          mode={mode}
          errorWhenEmpty={errorWhenEmpty}
        />
      </div>
      {isShown
        ? portalTarget
          ? createPortal(
              <div className="datePickerOverlayWrapper open">
                <div ref={pickerModalRef} style={pickerPosition}>
                  <DayPicker
                    months={months}
                    month={value ? new Date(value) : undefined}
                    weekdaysLong={weekdaysLong}
                    weekdaysShort={weekdaysShort}
                    firstDayOfWeek={1}
                    initialMonth={initialMonth}
                    disabledDays={
                      disabledDaysBefore ? { before: new Date() } : undefined
                    }
                    selectedDays={value ? new Date(value) : undefined}
                    onDayClick={onDayClickHandler}
                    numberOfMonths={1}
                    captionElement={({
                      date,
                      localeUtils,
                    }: CaptionElementProps) => {
                      return (
                        <YearMonthForm
                          date={date}
                          localeUtils={localeUtils}
                          onChange={handleYearMonthChange}
                          chosenDay={value ? new Date(value) : new Date()}
                        />
                      )
                    }}
                  />
                </div>
              </div>,
              portalTarget,
            )
          : null
        : null}
    </div>
  )
}

export default DatePicker
